'use client';

import { PaginationV2Links } from '@pickleballinc/react-ui';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { FavoriteArticleCard } from '@/modules/blog/article_components/FavoriteArticleCard';
import { PAGE_SIZE } from '@/modules/blog/constants';
import { RenderFavoritedNewsModule } from '@/types/homepage.types';

interface FavoriteArticlesGridProps {
  currentPage: number;
  categorySlug: string;
  favoriteArticles: RenderFavoritedNewsModule[];
  totalRecords: number;
}

export const FavoriteArticlesGrid = ({
  currentPage,
  categorySlug,
  favoriteArticles,
  totalRecords,
}: FavoriteArticlesGridProps) => {
  const router = useRouter();
  const totalArticles = favoriteArticles.length;
  const [articlesCount, setArticlesCount] = useState(totalArticles);
  const noFavoriteArticles = totalArticles === 0;

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);
  const getPageLink = (newPage: number) => {
    return `/${categorySlug}/favorites?page=${newPage}`;
  };

  const handleRefresh = () => {
    const newCount = articlesCount - 1;
    setArticlesCount(newCount);

    if (newCount === 0 && currentPage === totalPages) {
      router.replace(`?page=${currentPage - 1}`);
      return;
    }
    router.refresh();
  };

  return (
    <div
      className={clsx(
        'flex size-full flex-col items-center overflow-hidden px-2 sm:px-4',
        noFavoriteArticles && 'pt-4'
      )}
    >
      <div className='w-full max-w-[799px]'>
        <h1 className='pb-1 pl-4 pt-5 text-xl font-bold text-experiment sm:pb-5 sm:text-4xl lg:py-9 xl:pl-0'>
          My favorite articles
        </h1>
        {noFavoriteArticles ? (
          <div className='my-28'>
            <NoDataFound
              title='No articles found!'
              subtitle='Currently you have no favorite articles.'
            />
          </div>
        ) : null}
        <div
          className={clsx(
            'grid grid-cols-1 sm:grid-cols-2 justify-center gap-4 sm:gap-8',
            noFavoriteArticles && 'sm:pt-6 xl:pt-[96px]'
          )}
        >
          {favoriteArticles.map((favoriteArticle) => {
            return (
              <FavoriteArticleCard
                key={favoriteArticle.moduleUuid}
                favoriteArticle={favoriteArticle}
                onRefetch={handleRefresh}
              />
            );
          })}
        </div>
      </div>
      {totalRecords >= 1 ? (
        <div className='mb-4 flex size-full flex-col justify-end'>
          <div className='mb-4 mt-8 size-px w-full bg-gray-200 sm:mt-4' />

          <PaginationV2Links
            totalNumberOfPages={totalPages}
            currentPage={currentPage}
            previousLink={getPageLink(currentPage - 1)}
            nextLink={getPageLink(currentPage + 1)}
            getPageLink={(page) => getPageLink(page)}
          />
        </div>
      ) : null}
    </div>
  );
};
